import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import React, { useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks"
import { toggleEscalatedNoteResolutionModal } from "../../../../../app/slices/VerificationModuleSlice";
import { EscalatedNoteResolutionCommand, VerificationSaveCommand } from "../../../../../services/verifyService/commands";
import { Note } from "../../../../../services/verifyService/model/verification/Note";
import { addNote, editAttributes } from "../../../../../services/verifyService/model/verification/VerificationSlice";
import { useSaveEscalatedNoteResolutionMutation, useSaveVerificationMutation } from "../../../../../services/verifyService/verify-api";
import { ReduxUpdate } from "../../../../../utils/ReduxUpdateCommands";
import { Verification } from "../../../../../services/verifyService/model";

function EscalatedNoteResolution(){
    const dispatch = useAppDispatch();
    const modalOpen = useAppSelector((state) => state.verificationModule.EscalatedNoteResolutionModal);
    const verification = useAppSelector((state) => state.verification.verification);
    const [resolutionNote, setResolutionNote] = useState('');
    const [saveEscalatedNoteResolutionToServer] = useSaveEscalatedNoteResolutionMutation();
    const [saveVerification] = useSaveVerificationMutation();
    const [isSaving, setIsSaving] = useState(false);
    
    const saveNote = async () => {
        setIsSaving(true);
        try {
            let newNote = new Note();
            newNote.noteText = `FACILITY REQUEST RESOLVED - ${resolutionNote}`; 
            newNote.changeBy = 'You'; 
            newNote.changeDate = new Date();
            dispatch(addNote(newNote));
            // Uncheck the Facility Request toggle when resolved
            dispatch(editAttributes(new ReduxUpdate("isEscalated", false)));
            // First save the resolution event, then save verification to avoid race condition
            await saveEscalatedNoteResolutionToServer(new EscalatedNoteResolutionCommand(verification.id, resolutionNote)).unwrap();
            // Save the verification with isEscalated set to false
            const updatedVerification = { ...verification, verifyAttributes: { ...verification.verifyAttributes, isEscalated: false } } as Verification;
            await saveVerification(new VerificationSaveCommand(updatedVerification)).unwrap();
            setResolutionNote('');
            dispatch(toggleEscalatedNoteResolutionModal());
        } catch (error) {
            console.error('Error saving resolution:', error);
            // TODO: Add user-facing error notification (e.g., toast/snackbar)
            alert('Failed to save resolution. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }

    const handleClose = () => {
        if (isSaving) return; // Prevent closing during save
        setResolutionNote('');
        dispatch(toggleEscalatedNoteResolutionModal());
    }

    return <React.Fragment>
        <Dialog 
            open={modalOpen} 
            onClose={isSaving ? undefined : handleClose} 
            maxWidth="md" 
            fullWidth
            disableEscapeKeyDown={isSaving}
        >
            <DialogTitle>Resolve Facility Request</DialogTitle>
            <DialogContent>
                <Alert severity="success" sx={{marginBottom:'20px'}}>
                    <AlertTitle>Resolution</AlertTitle>
                    Please provide comments to resolve this facility request.
                </Alert>
                <TextField 
                    label='Resolution Comments' 
                    value={resolutionNote} 
                    onChange={(e) => setResolutionNote(e.target.value)} 
                    multiline 
                    required
                    fullWidth
                    InputLabelProps={{shrink:true}} 
                    sx={{marginTop:'10px'}} 
                    minRows='3'
                    placeholder="Enter your resolution comments..."
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${resolutionNote.length}/1000 characters`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
                <Button 
                    onClick={saveNote}
                    disabled={!resolutionNote.trim() || isSaving}
                    variant="contained"
                    color="success"
                >
                    {isSaving ? 'Saving...' : 'Mark Resolved'}
                </Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}

export { EscalatedNoteResolution }
